/**
 * NoemaTrajectoryView — Timeline Sparkline
 *
 * Shows per-sentence act category distribution as stacked bars,
 * with object introductions as markers and a tension curve overlay.
 * When sentencing v2 data is available, role colors show as background.
 *
 * @module @humanizer/studio/components/tools/NoemaTrajectoryView
 */

import React, { useMemo, useState } from 'react';
import type {
  NoemaGraphResult, NoemaTrajectoryPoint, NoeticActCategory,
} from '../../types/analysis';
import type { SentencingV2Result } from '../../types/analysis';

// -----------------------------------------------------------------------
// Constants
// -----------------------------------------------------------------------

const CATEGORY_COLORS: Record<NoeticActCategory, string> = {
  constitutive: 'var(--noema-constitutive)',
  temporal: 'var(--noema-temporal)',
  evaluative: 'var(--noema-evaluative)',
  epistemic: 'var(--noema-epistemic)',
  relational: 'var(--noema-relational)',
};

const CATEGORIES: NoeticActCategory[] = ['constitutive', 'temporal', 'evaluative', 'epistemic', 'relational'];

const ROLE_BG_COLORS: Record<string, string> = {
  anchor: 'rgba(var(--role-anchor-rgb, 59 130 246), 0.08)',
  advance: 'rgba(var(--role-advance-rgb, 34 197 94), 0.08)',
  ground: 'rgba(var(--role-ground-rgb, 148 163 184), 0.08)',
  pivot: 'rgba(var(--role-pivot-rgb, 234 179 8), 0.08)',
  echo: 'rgba(var(--role-echo-rgb, 168 85 247), 0.08)',
  bridge: 'rgba(var(--role-bridge-rgb, 6 182 212), 0.08)',
  scaffold: 'rgba(var(--role-scaffold-rgb, 100 116 139), 0.08)',
};

// -----------------------------------------------------------------------
// Props
// -----------------------------------------------------------------------

export interface NoemaTrajectoryViewProps {
  data: NoemaGraphResult;
  sentencingData?: SentencingV2Result | null;
  className?: string;
}

// -----------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------

export function NoemaTrajectoryView({
  data,
  sentencingData,
  className = '',
}: NoemaTrajectoryViewProps): React.ReactElement {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const trajectory = data.trajectory;
  const totalSentences = trajectory.length;

  if (totalSentences === 0) {
    return <div className={`noema-trajectory ${className}`}>No trajectory data</div>;
  }

  // Compute max acts per sentence for normalization
  const maxActs = useMemo(() => {
    let max = 1;
    for (const point of trajectory) {
      const total = CATEGORIES.reduce((s, c) => s + (point.actDistribution[c] || 0), 0);
      if (total > max) max = total;
    }
    return max;
  }, [trajectory]);

  // Build object introduction map
  const objectIntros = useMemo(() => {
    const map = new Map<number, string[]>();
    for (const point of trajectory) {
      if (point.objectsIntroduced.length > 0) {
        const labels = point.objectsIntroduced.map(id => {
          const obj = data.objects.find(o => o.id === id);
          return obj?.label || id;
        });
        map.set(point.sentenceIndex, labels);
      }
    }
    return map;
  }, [trajectory, data.objects]);

  // Build role map from sentencing data
  const roleMap = useMemo(() => {
    if (!sentencingData) return new Map<number, string>();
    const map = new Map<number, string>();
    for (const s of sentencingData.sentences) {
      map.set(s.index, s.role);
    }
    return map;
  }, [sentencingData]);

  const barWidth = Math.max(4, Math.min(20, 600 / totalSentences));
  const svgWidth = totalSentences * barWidth;
  const svgHeight = 120;
  const barAreaHeight = 80;

  // Tension curve points
  const tensionPoints = useMemo(() => {
    return trajectory.map((p, i) => {
      const x = i * barWidth + barWidth / 2;
      const y = svgHeight - 10 - p.tension * (svgHeight - 20);
      return `${x},${y}`;
    }).join(' ');
  }, [trajectory, barWidth, svgHeight]);

  return (
    <div className={`noema-trajectory ${className}`}>
      <div className="noema-trajectory__header">
        Intentional Trajectory ({totalSentences} sentences)
      </div>

      <div className="noema-trajectory__scroll">
        <svg
          className="noema-trajectory__svg"
          viewBox={`0 0 ${svgWidth} ${svgHeight}`}
          width={Math.max(svgWidth, 300)}
          height={svgHeight}
        >
          {/* Role background stripes */}
          {trajectory.map((point, i) => {
            const role = roleMap.get(point.sentenceIndex);
            if (!role) return null;
            return (
              <rect
                key={`bg-${i}`}
                x={i * barWidth}
                y={0}
                width={barWidth}
                height={svgHeight}
                fill={ROLE_BG_COLORS[role] || 'transparent'}
              />
            );
          })}

          {/* Stacked act bars */}
          {trajectory.map((point, i) => {
            const x = i * barWidth;
            let yOffset = barAreaHeight;
            const total = CATEGORIES.reduce((s, c) => s + (point.actDistribution[c] || 0), 0);
            if (total === 0) return null;

            return (
              <g
                key={`bar-${i}`}
                onMouseEnter={() => setHoveredIndex(i)}
                onMouseLeave={() => setHoveredIndex(null)}
                style={{ cursor: 'pointer' }}
              >
                {CATEGORIES.map(cat => {
                  const count = point.actDistribution[cat] || 0;
                  if (count === 0) return null;
                  const height = (count / maxActs) * barAreaHeight;
                  yOffset -= height;
                  return (
                    <rect
                      key={cat}
                      x={x + 1}
                      y={yOffset}
                      width={Math.max(barWidth - 2, 2)}
                      height={height}
                      fill={CATEGORY_COLORS[cat]}
                      fillOpacity={hoveredIndex === i ? 0.9 : 0.6}
                      rx={1}
                    />
                  );
                })}
              </g>
            );
          })}

          {/* Object introduction markers (diamonds at top) */}
          {trajectory.map((point, i) => {
            if (point.objectsIntroduced.length === 0) return null;
            const cx = i * barWidth + barWidth / 2;
            return (
              <polygon
                key={`intro-${i}`}
                points={`${cx},2 ${cx + 4},6 ${cx},10 ${cx - 4},6`}
                fill="var(--color-warning, #eab308)"
                fillOpacity={0.8}
              />
            );
          })}

          {/* Tension curve */}
          <polyline
            points={tensionPoints}
            fill="none"
            stroke="var(--color-danger, #ef4444)"
            strokeWidth={1.5}
            strokeOpacity={0.6}
            strokeLinejoin="round"
          />
        </svg>
      </div>

      {/* Hover detail */}
      {hoveredIndex !== null && trajectory[hoveredIndex] && (
        <div className="noema-trajectory__detail">
          <span className="noema-trajectory__detail-index">
            S{hoveredIndex}
            {roleMap.has(hoveredIndex) && (
              <span className="noema-trajectory__detail-role">
                {' '}({roleMap.get(hoveredIndex)})
              </span>
            )}
          </span>
          <div className="noema-trajectory__detail-acts">
            {CATEGORIES.map(cat => {
              const count = trajectory[hoveredIndex].actDistribution[cat] || 0;
              if (count === 0) return null;
              return (
                <span key={cat} className="noema-trajectory__detail-chip">
                  <span
                    className="noema-trajectory__detail-dot"
                    style={{ backgroundColor: CATEGORY_COLORS[cat] }}
                  />
                  {cat}: {count}
                </span>
              );
            })}
          </div>
          {objectIntros.has(hoveredIndex) && (
            <div className="noema-trajectory__detail-intros">
              New: {objectIntros.get(hoveredIndex)!.join(', ')}
            </div>
          )}
          <div className="noema-trajectory__detail-metrics">
            tension: {(trajectory[hoveredIndex].tension * 100).toFixed(0)}% |
            novelty: {(trajectory[hoveredIndex].novelty * 100).toFixed(0)}%
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="noema-trajectory__legend">
        {CATEGORIES.map(cat => (
          <span key={cat} className="noema-trajectory__legend-item">
            <span
              className="noema-trajectory__legend-dot"
              style={{ backgroundColor: CATEGORY_COLORS[cat] }}
            />
            {cat}
          </span>
        ))}
        <span className="noema-trajectory__legend-item">
          <span className="noema-trajectory__legend-diamond" />
          new object
        </span>
        <span className="noema-trajectory__legend-item">
          <span className="noema-trajectory__legend-line" />
          tension
        </span>
      </div>
    </div>
  );
}

export default NoemaTrajectoryView;
